// ProposalDialog.tsx — the dialog chrome shared by every proposal modal
// (same look as StatusChangeModal / BeaconFormModal): grey shell, white inner
// card, round gradient icon in the header.

import React from 'react';
import { Dialog } from 'primereact/dialog';
import { AlertTriangle, CircleCheck, Info, LucideIcon } from 'lucide-react';
import { G, C } from '../../../components/GlobalComponents/uiTokens';

export const SHELL: React.CSSProperties = { background: G.shellBg, border: `1px solid ${G.shellBorder}`, borderRadius: '13px', padding: '4.6px' };
export const INNER: React.CSSProperties = { background: C.white, border: `1px solid ${G.line}`, borderRadius: '9.2px' };

export const FIELD_LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: '0.68rem',
  fontWeight: 700,
  color: C.teal,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '5.5px',
};

interface ProposalDialogProps {
  visible: boolean;
  onHide: () => void;
  title: string;
  subtitle?: React.ReactNode;
  icon: LucideIcon;
  /** danger turns the header icon red (delete confirmations) */
  tone?: 'teal' | 'danger';
  /** true while saving — the dialog can't be closed */
  busy?: boolean;
  maxWidth?: string;
  children: React.ReactNode;
  /** buttons row, shown under the inner card */
  footer?: React.ReactNode;
}

const ProposalDialog: React.FC<ProposalDialogProps> = ({
  visible,
  onHide,
  title,
  subtitle,
  icon: Icon,
  tone = 'teal',
  busy = false,
  maxWidth = '520px',
  children,
  footer,
}) => {
  const handleHide = () => {
    if (!busy) onHide();
  };

  const header = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          flexShrink: 0,
          background: tone === 'danger' ? 'linear-gradient(135deg,#c62828,#e53935)' : `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
          color: tone === 'danger' ? '#fff' : C.lime,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: tone === 'danger' ? '0 6px 18px rgba(198,40,40,0.28)' : '0 6px 18px rgba(17,85,80,0.25)',
        }}
      >
        <Icon size={20} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#111827', lineHeight: 1.2, marginBottom: '3px' }}>{title}</div>
        {subtitle && <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>{subtitle}</div>}
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      header={header}
      className="pp-dialog"
      maskClassName="pp-mask"
      style={{ width: '92vw', maxWidth }}
      modal
      closable={!busy}
      dismissableMask={!busy}
      draggable={false}
      resizable={false}
      blockScroll
    >
      <style>{`
        .pp-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; animation: ppMaskIn 0.3s ease; }
        .pp-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; animation: ppDlgIn 0.3s cubic-bezier(0.4,0,0.2,1); }
        .pp-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
        .pp-dialog .p-dialog-header-icon { color: ${C.sub}; }
        .pp-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; }
        @keyframes ppMaskIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ppDlgIn { from { opacity: 0; transform: scale(0.94) translateY(-14px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        .pp-spin { animation: ppSpin 0.9s linear infinite; }
        @keyframes ppSpin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) { .pp-spin { animation: none; } }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        <div style={SHELL}>
          <div style={{ ...INNER, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '13px' }}>{children}</div>
        </div>
        {footer && <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '7.4px', flexWrap: 'wrap', marginTop: '2px' }}>{footer}</div>}
      </div>
    </Dialog>
  );
};

export default ProposalDialog;

// ── Small inline message ─────────────────────────────────────────────────────
const NOTICE_TONES = {
  error: { bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.3)', color: '#c62828', icon: AlertTriangle },
  warn: { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.35)', color: '#b45309', icon: AlertTriangle },
  info: { bg: 'rgba(17,85,80,0.06)', border: 'rgba(17,85,80,0.18)', color: C.teal, icon: Info },
  ok: { bg: 'rgba(22,163,74,0.1)', border: 'rgba(22,163,74,0.3)', color: '#15803d', icon: CircleCheck },
};

export const Notice: React.FC<{ tone: keyof typeof NOTICE_TONES; children: React.ReactNode }> = ({ tone, children }) => {
  const t = NOTICE_TONES[tone];
  const Icon = t.icon;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '9px 11px', background: t.bg, border: `1px solid ${t.border}`, borderRadius: '8.3px' }}>
      <Icon size={14} color={t.color} style={{ flexShrink: 0, marginTop: '1px' }} />
      <span style={{ fontSize: '0.73rem', color: t.color, fontWeight: 600, lineHeight: 1.5 }}>{children}</span>
    </div>
  );
};
